import React, { useCallback, useRef, useState } from 'react';
import { Button, Tooltip } from '@shopify/polaris';
import { ordersService } from '../../services/ordersService';
import { getEditAvailability } from '../../utils/orderEditRules';
import { isAddonLine } from '../../utils/orderAddons';

// A disabled button swallows hover events, so the span carries the tooltip.
export function GatedButton({ allowed, reason, label, onOpen, size }) {
  if (allowed) {
    return (
      <Button size={size} onClick={onOpen}>
        {label}
      </Button>
    );
  }
  return (
    <Tooltip content={reason || 'Not available for this order'}>
      <span>
        <Button size={size} disabled ariaDisabled>
          {label}
        </Button>
      </span>
    </Tooltip>
  );
}

export function AddStoryButton({ order, canManageOrders, onOpen }) {
  if (!canManageOrders || !order) return null;
  const { canAdd, addReason } = getEditAvailability(order);
  return (
    <GatedButton
      allowed={canAdd}
      reason={addReason}
      label="Add story"
      onOpen={onOpen}
      size="slim"
    />
  );
}

export function ReplaceStoryButton({ order, lineItem, canManageOrders, onOpen }) {
  if (!canManageOrders || !order || !lineItem || isAddonLine(lineItem)) {
    return null;
  }
  const { canReplace, replaceReason } = getEditAvailability(order);
  return (
    <GatedButton
      allowed={canReplace}
      reason={replaceReason}
      label="Replace story"
      onOpen={() => onOpen(lineItem)}
      size="slim"
    />
  );
}

// Loads the story catalog once on first use and shares the in-flight request.
export function useStoryCatalog() {
  const [catalog, setCatalog] = useState(null);
  const [catalogError, setCatalogError] = useState(null);
  const catalogPromise = useRef(null);

  const loadCatalog = useCallback(async () => {
    if (!catalogPromise.current) {
      catalogPromise.current = ordersService.getStoryCatalog();
    }
    try {
      const loaded = await catalogPromise.current;
      setCatalog(loaded);
      setCatalogError(null);
      return loaded;
    } catch (error) {
      catalogPromise.current = null;
      setCatalogError(error);
      return null;
    }
  }, []);

  return { catalog, catalogError, loadCatalog };
}

export function useOrderItemEdit({ order, onSaved, storyCatalog }) {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState('add');
  const [lineItem, setLineItem] = useState(null);
  const ownCatalog = useStoryCatalog();
  const { catalog, catalogError, loadCatalog } = storyCatalog || ownCatalog;

  const open = useCallback(async (nextMode, item) => {
    if (!(await loadCatalog())) return;
    setMode(nextMode);
    setLineItem(item || null);
    setIsOpen(true);
  }, [loadCatalog]);

  const openAdd = useCallback(() => open('add', null), [open]);
  const openReplace = useCallback(item => open('replace', item), [open]);

  const onClose = useCallback(() => setIsOpen(false), []);
  const onSubmitted = useCallback(
    result => {
      setIsOpen(false);
      if (onSaved) onSaved(result, mode);
    },
    [onSaved, mode]
  );

  return {
    modalProps: { isOpen, mode, order, lineItem, catalog, onClose, onSubmitted },
    openAdd,
    openReplace,
    catalogError
  };
}

export function buildEditToast(result, mode) {
  const parts = [mode === 'replace' ? 'Story replaced' : 'Story added'];
  if (Array.isArray(result?.warnings) && result.warnings.length) {
    parts.push(result.warnings.join('; '));
  }
  if (result?.shopify_synced === false) {
    parts.push('Shopify was not updated for this order');
  }
  return parts.join('. ');
}

export function buildFullEditToast(result, headline = 'Changes saved') {
  const parts = [headline];
  if (Array.isArray(result?.warnings) && result.warnings.length) {
    parts.push(result.warnings.join('; '));
  }
  if (result?.shopify_synced === false) {
    parts.push('Shopify was not updated for this order');
  }
  return parts.join('. ');
}
